import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ClassificationChoice,
  ClassificationResponse,
} from "@patent-studio/api-types";
import { classificationKey, cpcSearchKey } from "@/hooks/query-keys";
import { ApiError } from "@/services/api-client";
import {
  chooseClassification,
  readClassification,
  requestClassificationRun,
  searchCpc,
} from "@/services/classification";

/** How often the page asks again while codes are being suggested. */
export const RUNNING_POLL_MS = 2000;

/** The shortest query a search of the scheme runs on, as the server reads it. */
export const SHORTEST_CPC_QUERY = 2;

/**
 * The task's classification, read again every two seconds while a run is suggesting codes,
 * in a hidden tab too, so the run's landing is there when the attorney comes back.
 */
export function useClassification(taskId: string) {
  return useQuery({
    queryKey: classificationKey(taskId),
    queryFn: () => readClassification(taskId),
    refetchInterval: (query) =>
      query.state.data?.run.status === "running" ? RUNNING_POLL_MS : false,
    refetchIntervalInBackground: true,
  });
}

/**
 * Store the attorney's codes, and confirm them when asked.
 *
 * A conflict means the classification moved under the attorney, a run landing
 * its suggestions among them; the reading is fetched again so the page shows
 * what stands.
 */
export function useChooseClassification(taskId: string) {
  const client = useQueryClient();
  const key = classificationKey(taskId);
  return useMutation({
    mutationFn: (choice: ClassificationChoice) =>
      chooseClassification(taskId, choice),
    onSuccess: (reading) => {
      client.setQueryData<ClassificationResponse>(key, reading);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.info(
          "The classification changed since you looked, so it has been refreshed.",
        );
        void client.invalidateQueries({ queryKey: key });
        return;
      }
      toast.error("The classification could not be saved. Try again.");
    },
  });
}

/** Ask for codes from the disclosure as it now stands; the reading then follows the run. */
export function useRequestClassificationRun(taskId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => requestClassificationRun(taskId),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: classificationKey(taskId) });
    },
  });
}

/** Codes of the scheme whose symbol starts with the query or whose title holds every word of it. */
export function useCpcSearch(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: cpcSearchKey(trimmed),
    queryFn: () => searchCpc(trimmed),
    enabled: trimmed.length >= SHORTEST_CPC_QUERY,
    staleTime: Infinity,
  });
}
